import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateProfile } from '@/features/profile/hooks/useUpdateProfile';
import { useProfilePhoto } from '@/features/profile/hooks/useProfilePhoto';
import { useToast } from '@/components/ui/use-toast';
import { validateUsername } from '@/utils/usernameValidation';
import { useSubscriptionStatus } from '@/features/subscription/hooks/useSubscriptionStatus';
import { useCancelSubscriptionAtPeriodEnd } from '@/features/subscription/hooks/useManageSubscription';

// ─── Schema ──────────────────────────────────────────────────────────────────

const editProfileSchema = z.object({
  full_name: z.string().min(2, 'Name must be at least 2 characters'),
  username: z.string().min(4, 'Username must be at least 4 characters').max(32),
  belt_rank: z.string(),
  main_academy_id: z.string().nullable().optional(),
  main_academy_name: z.string().optional(),
  is_instructor: z.boolean(),
  country: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
  profession: z.string().optional(),
  business_type: z.string().optional(),
  instagram: z.string().optional(),
  x: z.string().optional(),
  facebook: z.string().optional(),
  youtube: z.string().optional(),
  website: z.string().optional(),
  linkedin: z.string().optional(),
});

export type EditProfileFormValues = z.infer<typeof editProfileSchema>;

// ─── Username check status ────────────────────────────────────────────────────

export type UsernameStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid' | 'unchanged';

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useEditProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const updateProfile = useUpdateProfile();
  const { uploadPhoto, state: photoState } = useProfilePhoto();
  const { toast } = useToast();
  const { isPro } = useSubscriptionStatus();
  const cancelProMutation = useCancelSubscriptionAtPeriodEnd();

  // Separate state for multi-select fields (not simple RHF fields)
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedTechniques, setSelectedTechniques] = useState<Record<string, string[]>>({});
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameDebounceTimer, setUsernameDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [showInstructorConfirm, setShowInstructorConfirm] = useState(false);
  const [pendingInstructorValue, setPendingInstructorValue] = useState<boolean | null>(null);
  // SOS-355 §2 — separate dialog for instructor→practitioner WITH active Pro.
  const [showProDowngradeDialog, setShowProDowngradeDialog] = useState(false);

  // ─── Fetch full profile data ──────────────────────────────────────────────

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['edit-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const [profileResult, interestsResult, techniquesResult] = await Promise.all([
        supabase
          .from('profiles')
          .select(`
            id, full_name, username, belt_rank, is_instructor,
            profile_picture_url, country, state, city,
            profession, business_type_id,
            main_academy_id,
            academies:main_academy_id (id, name),
            business_types:business_type_id (name),
            instagram, x, facebook, youtube, website, linkedin
          `)
          .eq('id', user.id)
          .single(),
        supabase
          .from('profile_hobbies')
          .select('hobbies (name)')
          .eq('profile_id', user.id),
        supabase
          .from('profile_favorite_techniques')
          .select('techniques (name, technique_categories (name))')
          .eq('profile_id', user.id),
      ]);

      const profile = profileResult.data;
      const interests = (interestsResult.data || [])
        .map((r: any) => r.hobbies?.name)
        .filter(Boolean) as string[];

      const techniques: Record<string, string[]> = {};
      (techniquesResult.data || []).forEach((r: any) => {
        const cat = r.techniques?.technique_categories?.name;
        const name = r.techniques?.name;
        if (cat && name) {
          if (!techniques[cat]) techniques[cat] = [];
          techniques[cat].push(name);
        }
      });

      return { profile, interests, techniques };
    },
    enabled: !!user?.id,
  });

  // ─── React Hook Form ──────────────────────────────────────────────────────

  const form = useForm<EditProfileFormValues>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      full_name: '',
      username: '',
      belt_rank: 'white',
      main_academy_id: null,
      main_academy_name: '',
      is_instructor: false,
      country: '',
      state: '',
      city: '',
      profession: '',
      business_type: '',
    },
  });

  // Populate form once data loads
  useEffect(() => {
    if (!profileData?.profile) return;
    const p = profileData.profile;

    form.reset({
      full_name: p.full_name || '',
      username: p.username || '',
      belt_rank: p.belt_rank || 'white',
      main_academy_id: p.main_academy_id || null,
      main_academy_name: (p.academies as any)?.name || '',
      is_instructor: p.is_instructor || false,
      country: p.country || '',
      state: p.state || '',
      city: p.city || '',
      profession: p.profession || '',
      business_type: (p.business_types as any)?.name || '',
      instagram: (p as any).instagram || '',
      x: (p as any).x || '',
      facebook: (p as any).facebook || '',
      youtube: (p as any).youtube || '',
      website: (p as any).website || '',
      linkedin: (p as any).linkedin || '',
    });

    setSelectedInterests(profileData.interests);
    setSelectedTechniques(profileData.techniques);
    setUsernameStatus('idle');
  }, [profileData]);

  // ─── Username validation with debounce ───────────────────────────────────

  const checkUsername = useCallback(async (value: string) => {
    const originalUsername = profileData?.profile?.username;
    if (value === originalUsername) {
      setUsernameStatus('unchanged');
      return;
    }
    if (!validateUsername(value)) {
      setUsernameStatus('invalid');
      return;
    }
    setUsernameStatus('checking');
    const { data } = await supabase.rpc('check_username_available', { input_username: value });
    setUsernameStatus(data ? 'available' : 'unavailable');
  }, [profileData?.profile?.username]);

  const handleUsernameChange = useCallback((value: string) => {
    form.setValue('username', value, { shouldDirty: true });
    setUsernameStatus('idle');
    if (usernameDebounceTimer) clearTimeout(usernameDebounceTimer);
    const timer = setTimeout(() => checkUsername(value), 600);
    setUsernameDebounceTimer(timer);
  }, [form, checkUsername, usernameDebounceTimer]);

  // ─── Profile type change (with confirmation) ─────────────────────────────

  const handleInstructorToggle = useCallback((newValue: boolean) => {
    const current = form.getValues('is_instructor');
    if (newValue === current) return;

    // SOS-355 §2 — leaving instructor role with an active Pro subscription
    // requires the user to consciously handle the recurring charge first.
    if (current === true && newValue === false && isPro) {
      setShowProDowngradeDialog(true);
      return;
    }

    setPendingInstructorValue(newValue);
    setShowInstructorConfirm(true);
  }, [form, isPro]);

  const confirmInstructorChange = useCallback(() => {
    if (pendingInstructorValue !== null) {
      form.setValue('is_instructor', pendingInstructorValue, { shouldDirty: true });
    }
    setShowInstructorConfirm(false);
    setPendingInstructorValue(null);
  }, [form, pendingInstructorValue]);

  const cancelInstructorChange = useCallback(() => {
    setShowInstructorConfirm(false);
    setPendingInstructorValue(null);
  }, []);

  // ─── Pro downgrade dialog handlers (SOS-355 §2) ──────────────────────────

  const confirmDowngradeAndCancelPro = useCallback(async () => {
    try {
      await cancelProMutation.mutateAsync({ reason: null });
      form.setValue('is_instructor', false, { shouldDirty: true });
      setShowProDowngradeDialog(false);
    } catch (error: any) {
      toast({
        title: 'Could not cancel Pro',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  }, [cancelProMutation, form, toast]);

  const confirmDowngradeKeepPro = useCallback(() => {
    form.setValue('is_instructor', false, { shouldDirty: true });
    setShowProDowngradeDialog(false);
  }, [form]);

  const dismissProDowngradeDialog = useCallback(() => {
    setShowProDowngradeDialog(false);
  }, []);

  // ─── Save interests ───────────────────────────────────────────────────────

  const saveInterests = async (interests: string[]) => {
    if (!user?.id) return;
    await supabase.from('profile_hobbies').delete().eq('profile_id', user.id);
    if (interests.length === 0) return;
    const { data: hobbies } = await supabase
      .from('hobbies')
      .select('id, name')
      .in('name', interests);
    if (hobbies?.length) {
      await supabase.from('profile_hobbies').insert(
        hobbies.map(h => ({ profile_id: user.id, hobby_id: h.id }))
      );
    }
  };

  // ─── Save techniques ──────────────────────────────────────────────────────

  const saveTechniques = async (techniques: Record<string, string[]>) => {
    if (!user?.id) return;
    await supabase.from('profile_favorite_techniques').delete().eq('profile_id', user.id);
    const allNames = Object.values(techniques).flat();
    if (allNames.length === 0) return;
    const { data: techs } = await supabase
      .from('techniques')
      .select('id, name')
      .in('name', allNames);
    if (techs?.length) {
      await supabase.from('profile_favorite_techniques').insert(
        techs.map(t => ({ profile_id: user.id, technique_id: t.id }))
      );
    }
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const onSubmit = form.handleSubmit(async (values) => {
    const isUsernameBlocked =
      usernameStatus === 'unavailable' || usernameStatus === 'invalid';
    if (isUsernameBlocked) {
      form.setError('username', { message: 'Username is not available' });
      return;
    }

    try {
      await Promise.all([
        updateProfile.mutateAsync({
          full_name: values.full_name,
          username: values.username.toLowerCase(),
          // belt_rank is intentionally excluded — updated only via promotions flow
main_academy_id: values.main_academy_id || null,
          is_instructor: values.is_instructor,
          country: values.country,
          state: values.state,
          city: values.city,
          profession: values.profession,
          business_type: values.business_type,
          instagram: values.instagram,
          x: values.x,
          facebook: values.facebook,
          youtube: values.youtube,
          website: values.website,
          linkedin: values.linkedin,
        }),
        saveInterests(selectedInterests),
        saveTechniques(selectedTechniques),
      ]);

      queryClient.invalidateQueries({ queryKey: ['edit-profile', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      // SOS-355 §3 — keeps the PLAN row, paywall and feature gates in sync
      // immediately after a role change persists.
      queryClient.invalidateQueries({ queryKey: ['my-account-profile'] });

      toast({ title: 'Profile updated successfully!' });
    } catch (error: any) {
      toast({
        title: 'Error updating profile',
        description: error?.message || String(error),
        variant: 'destructive',
      });
    }
  });

  // ─── Derived state ────────────────────────────────────────────────────────

  const isSaving = updateProfile.isPending || photoState.isUploading;
  const canSave = form.formState.isDirty || !!(
    selectedInterests.toString() !== profileData?.interests?.toString()
  );

  return {
    form,
    isLoading,
    isSaving,
    canSave,
    profilePictureUrl: profileData?.profile?.profile_picture_url ?? null,
    // Username
    usernameStatus,
    handleUsernameChange,
    // Interests
    selectedInterests,
    setSelectedInterests,
    // Techniques
    selectedTechniques,
    setSelectedTechniques,
    // Profile type confirmation
    showInstructorConfirm,
    pendingInstructorValue,
    handleInstructorToggle,
    confirmInstructorChange,
    cancelInstructorChange,
    // Pro downgrade confirmation (SOS-355 §2)
    showProDowngradeDialog,
    isCancelingPro: cancelProMutation.isPending,
    confirmDowngradeAndCancelPro,
    confirmDowngradeKeepPro,
    dismissProDowngradeDialog,
    // Photo
    uploadPhoto,
    photoState,
    // Submit
    onSubmit,
  };
};
